import {
  Chip,
  IconButton,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Email } from "@mui/icons-material";

import React, { FC, useState } from "react";
import { ROLES, User } from "../../../../types/user";
import ConfirmActionModal from "../../../../modals/ConfirmActionModal/ConfirmActionModal";
import { resetPasswordRequestThunk } from "../../../../reduxStore/auth/thunks";
import { showNotificationThunk } from "../../../../reduxStore/alert/thunks";
import { NotificationSeverity } from "../../../../helpers/constants";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import { userSelector } from "../../../../reduxStore/auth/selectors";
import { UserWithPet } from "components/VetStore/CoachPetAvatar";

type UserItemProps = {
  user: any;
  displayEmailSendInvitation?: boolean;
};

export const UserItem: FC<UserItemProps> = ({
  user,
  displayEmailSendInvitation = true,
}) => {
  const [inviteUserModal, setInviteUserModal] = useState(false);
  const [userToInvite, setUserToInvite] = useState<User | undefined>(undefined);
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(userSelector);

  const getSecondaryAction = () => {
    if (
      currentUser &&
      [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER].includes(currentUser.role)
    ) {
      return (
        <Tooltip title="Resend Email Invitation">
          <IconButton
            edge="end"
            aria-label="comments"
            onClick={() => {
              setUserToInvite(user);
              setInviteUserModal(true);
            }}
          >
            <Email />
          </IconButton>
        </Tooltip>
      );
    }
    return null;
  };

  const sendInvitation = async (email: string) => {
    await dispatch(
      resetPasswordRequestThunk({ email, isResendingInvite: true }),
    );
    dispatch(
      showNotificationThunk({
        message: "Invitation sent successfully",
        severity: NotificationSeverity.success,
      }),
    );
  };

  return (
    <ListItem
      secondaryAction={displayEmailSendInvitation ? getSecondaryAction() : null}
    >
      <ListItemAvatar sx={{ minWidth: 88 }}>
        <UserWithPet
          pictureUrl={user.picture_url}
          name={user.name}
          equippedStoreItems={user.equipped_store_items}
          avatarSize={40}
        />
      </ListItemAvatar>
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography component="span" variant="body1">
              {user.name}
            </Typography>
            <Chip
              label={
                user.accepted_terms_of_use ? "Terms accepted" : "Terms pending"
              }
              size="small"
              color={user.accepted_terms_of_use ? "success" : "warning"}
              variant="outlined"
              sx={{
                height: 18,
                fontSize: "0.65rem",
                "& .MuiChip-label": {
                  px: 0.75,
                  py: 0,
                },
              }}
            />
          </Stack>
        }
        secondary={
          <Link
            component={"a"}
            variant="body2"
            color="rgba(0,0,0,0.75)"
            href={`mailto:${user.email}`}
          >
            {user.email}
          </Link>
        }
      />
      {inviteUserModal && userToInvite && (
        <ConfirmActionModal
          title={"Invite User"}
          message={`A formal email invitation will be sent to the email address ${userToInvite?.email} `}
          show={inviteUserModal}
          onConfirm={() => sendInvitation(userToInvite?.email ?? "")}
          onDismiss={() => {
            setInviteUserModal(false);
          }}
        />
      )}
    </ListItem>
  );
};
